<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';

// Conexión de WhatsApp API.
//
// Hay dos caminos y el cliente ve uno solo:
//
//   · Embedded Signup (recomendado): pulsa un botón, Meta abre su propio
//     asistente en una ventana, y nosotros recibimos un código con el que
//     dejamos la bandeja lista. Requiere que la app esté aprobada por Meta.
//   · Formulario manual: los tres identificadores a mano. Sigue existiendo
//     porque hay clientes que ya tienen su número en otra plataforma y solo
//     quieren mudarlo, y porque si Meta se cae hay que poder seguir vendiendo.
//
// El servidor decide cuál se ofrece; el navegador nunca ve el App Secret.

const SDK_ID = 'facebook-jssdk';
const ORIGEN_META = 'facebook.com';

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
      // Estado del camino automático
      conexion: { habilitado: false, app_id: null, config_id: null },
      cargandoConfig: true,
      conectando: false,
      verFormularioManual: false,
      datosDeMeta: null,
      manejadorDeMensajes: null,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    urlConexion() {
      return `/api/v1/accounts/${this.$route.params.accountId}/chatea_whatsapp_connection`;
    },
    mostrarBotonMeta() {
      return this.conexion.habilitado && !this.verFormularioManual;
    },
  },
  mounted() {
    this.cargarConfiguracion();
  },
  beforeUnmount() {
    // Si el cliente se va a mitad del asistente, el escuchador no puede quedarse
    // vivo: capturaría mensajes de otra pantalla.
    this.dejarDeEscucharAMeta();
  },
  methods: {
    async cargarConfiguracion() {
      try {
        const { data } = await window.axios.get(this.urlConexion);
        this.conexion = data || {};
      } catch (error) {
        // Si no podemos preguntar, se cae al formulario manual sin avisar:
        // el cliente igual puede conectar y no le sirve de nada el detalle.
        this.conexion = { habilitado: false };
      } finally {
        this.cargandoConfig = false;
      }
    },

    cargarSdkDeFacebook() {
      return new Promise((resolver, rechazar) => {
        if (window.FB) {
          resolver();
          return;
        }
        if (document.getElementById(SDK_ID)) {
          // Ya se está cargando por otra llamada: se espera a que termine.
          const espera = setInterval(() => {
            if (window.FB) {
              clearInterval(espera);
              resolver();
            }
          }, 100);
          setTimeout(() => {
            clearInterval(espera);
            if (!window.FB) rechazar(new Error('sdk'));
          }, 10000);
          return;
        }

        const etiqueta = document.createElement('script');
        etiqueta.id = SDK_ID;
        etiqueta.src = 'https://connect.facebook.net/en_US/sdk.js';
        etiqueta.async = true;
        etiqueta.defer = true;
        etiqueta.crossOrigin = 'anonymous';
        etiqueta.onload = () => resolver();
        etiqueta.onerror = () => rechazar(new Error('sdk'));
        document.body.appendChild(etiqueta);
      });
    },

    // Meta manda el identificador del número y de la cuenta por postMessage,
    // aparte del código que llega por el callback. Hacen falta los dos.
    escucharAMeta() {
      this.dejarDeEscucharAMeta();
      this.manejadorDeMensajes = evento => {
        let origen = '';
        try {
          origen = new URL(evento.origin).hostname;
        } catch (error) {
          return;
        }
        if (origen !== ORIGEN_META && !origen.endsWith(`.${ORIGEN_META}`)) {
          return;
        }

        let datos = null;
        try {
          datos = JSON.parse(evento.data);
        } catch (error) {
          return;
        }

        if (datos?.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (datos.event === 'FINISH') {
          this.datosDeMeta = {
            waba_id: datos.data?.waba_id,
            phone_number_id: datos.data?.phone_number_id,
          };
        }
      };
      window.addEventListener('message', this.manejadorDeMensajes);
    },

    dejarDeEscucharAMeta() {
      if (this.manejadorDeMensajes) {
        window.removeEventListener('message', this.manejadorDeMensajes);
        this.manejadorDeMensajes = null;
      }
    },

    async conectarConMeta() {
      this.conectando = true;
      this.datosDeMeta = null;

      // Camino preferido: la página que aloja Meta. Se navega de verdad, sin
      // ventana emergente, porque los bloqueadores del navegador y los móviles
      // se comen las emergentes y el cliente se queda mirando un botón muerto.
      // Meta nos devuelve a /whatsapp/conectado con el código.
      if (this.conexion.enlace) {
        window.location.href = this.conexion.enlace;
        return;
      }

      try {
        await this.cargarSdkDeFacebook();
        window.FB.init({
          appId: this.conexion.app_id,
          cookie: true,
          xfbml: false,
          version: this.conexion.version_api || 'v21.0',
        });
        this.escucharAMeta();

        const respuesta = await new Promise(resolver => {
          window.FB.login(resolver, {
            config_id: this.conexion.config_id,
            // Pedimos un código, no un token: el canje ocurre en el servidor,
            // que es el único que tiene el App Secret.
            response_type: 'code',
            override_default_response_type: true,
            extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
          });
        });

        const codigo = respuesta?.authResponse?.code;
        if (!codigo) {
          // Cerrar la ventana de Meta no es un error: es una decisión.
          this.conectando = false;
          this.dejarDeEscucharAMeta();
          return;
        }

        await this.registrarConexion(codigo);
      } catch (error) {
        this.conectando = false;
        this.dejarDeEscucharAMeta();
        useAlert(
          'No pudimos abrir el asistente de Meta. Revisa que tu navegador no esté bloqueando ventanas emergentes.'
        );
      }
    },

    async registrarConexion(codigo) {
      try {
        const { data } = await window.axios.post(this.urlConexion, {
          code: codigo,
          waba_id: this.datosDeMeta?.waba_id,
          phone_number_id: this.datosDeMeta?.phone_number_id,
          nombre: this.inboxName || undefined,
        });
        this.dejarDeEscucharAMeta();
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.inbox_id },
        });
      } catch (error) {
        this.conectando = false;
        this.dejarDeEscucharAMeta();
        useAlert(
          error?.response?.data?.error ||
            'No pudimos completar la conexión. Inténtalo otra vez.'
        );
      }
    },

    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName,
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'whatsapp_cloud',
              provider_config: {
                api_key: this.apiKey,
                phone_number_id: this.phoneNumberId,
                business_account_id: this.businessAccountId,
              },
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiKey: { required },
    phoneNumberId: { required, isNumber },
    businessAccountId: { required, isNumber },
  },
};
</script>

<template>
  <div>
    <!-- Camino automático -->
    <div v-if="mostrarBotonMeta" class="w-[65%]">
      <div
        class="p-6 mb-4 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <h4 class="mb-1 text-base font-semibold">
          Conecta tu WhatsApp en un minuto
        </h4>
        <p class="mb-4 text-sm text-slate-600 dark:text-slate-300">
          Te abriremos el asistente de Meta para que autorices tu número. No
          necesitas crear nada en Facebook ni copiar códigos: cuando termines,
          tu bandeja queda lista.
        </p>

        <!-- El nombre solo se pide en el camino que no sale de esta página: si
             navegamos a Meta, lo escrito aquí se perdería y quedaría un campo
             que no sirve para nada. La bandeja se nombra con el número
             verificado y el cliente puede renombrarla después. -->
        <label v-if="!conexion.enlace" class="mb-4 block">
          <span class="text-sm">Nombre de la bandeja (opcional)</span>
          <input
            v-model="inboxName"
            type="text"
            placeholder="Ej. Ventas WhatsApp"
          />
        </label>

        <woot-button
          :is-loading="conectando"
          :is-disabled="conectando"
          icon="brand-whatsapp"
          @click="conectarConMeta"
        >
          {{ conectando ? 'Conectando…' : 'Conectar con WhatsApp' }}
        </woot-button>

        <p class="mt-4 mb-0 text-xs text-slate-500">
          ¿Tu número ya está en otra plataforma y tienes tus propias claves?
          <button
            type="button"
            class="text-woot-600 underline bg-transparent border-0 cursor-pointer p-0"
            @click="verFormularioManual = true"
          >
            Conectarlo manualmente
          </button>
        </p>
      </div>
    </div>

    <!-- Camino manual -->
    <form
      v-else-if="!cargandoConfig"
      class="flex flex-wrap mx-0"
      @submit.prevent="createChannel()"
    >
      <div
        v-if="conexion.habilitado"
        class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%] mb-4"
      >
        <button
          type="button"
          class="text-woot-600 underline bg-transparent border-0 cursor-pointer p-0 text-sm"
          @click="verFormularioManual = false"
        >
          ← Volver a la conexión automática
        </button>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')
            "
            @blur="v$.phoneNumber.$touch"
          />
          <span v-if="v$.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.phoneNumberId.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.LABEL') }}
          </span>
          <input
            v-model="phoneNumberId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.PLACEHOLDER')
            "
            @blur="v$.phoneNumberId.$touch"
          />
          <span v-if="v$.phoneNumberId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.businessAccountId.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.LABEL') }}
          </span>
          <input
            v-model="businessAccountId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.PLACEHOLDER')
            "
            @blur="v$.businessAccountId.$touch"
          />
          <span v-if="v$.businessAccountId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.apiKey.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL') }}
          </span>
          <input
            v-model="apiKey"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
            @blur="v$.apiKey.$touch"
          />
          <span v-if="v$.apiKey.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <woot-submit-button
          :loading="uiFlags.isCreating"
          :button-text="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
